import {Validators} from '@angular/forms';

import * as moment from 'moment';

import {
    FundMandatoryProductPriceTypesInterface,
    FundProductPricesEnum,
    FundProductTypesEnum,
    ProductCategory
} from '@spa/api';

import {ComponentTypesEnum} from 'appShared/enums';
import {AmountControl, BaseControl, DatepickerControl, Helper} from 'appShared/classes';
import {DialogButtonTypesEnum, DialogConfigInterface} from '../../../app/shared/components';

import {environment} from 'appEnv/environment';
import {PanelSettingsProductsTrait} from "appEntries/panel/traits";

export const AddEditProductPriceDialogWidth = '500px';
export const AddEditProductPriceDateSelector = 'productPriceDate';
export const AddEditProductPriceIssuePriceSelector = 'productPriceIssuePrice';
export const AddEditProductPriceBidPriceSelector = 'productPriceBidPrice';
export const AddEditProductPricePenalRedemptionPriceSelector = 'productPricePenalRedemptionPrice';
export const AddEditProductPriceNetAssetValueSelector = 'productPriceNetAssetValue';
export const AddEditProductPriceAskPriceSelector = 'productPriceAskPrice';
export const AddEditProductYieldToMaturitySelector = 'productYieldToMaturity';
export const AddEditProductAccruedInterestSelector = 'productAccruedInterest';

export const AddEditProductPricesLabel = 'At least one price';

export const AddEditProductPriceDialogConfig = (isAddNew = false): DialogConfigInterface => {
    const buttons = [{type: DialogButtonTypesEnum.Cancel}];

    if (isAddNew) {
        buttons.push({type: DialogButtonTypesEnum.Submit});
    } else {
        buttons.push({type: DialogButtonTypesEnum.Update});
    }

    return {
        process: {
            loading: false,
            finished: false,
            failed: false,
            failedMessage: '',
            succeedMessage: '',
        },
        buttons,
        buttonsDisabled: false,
        buttonsHidden: false,
    };
};

export const AddEditProductPriceFormControls = (
    mandatoryTypes: FundMandatoryProductPriceTypesInterface,
    productType: FundProductTypesEnum,
    productCategory: ProductCategory ): BaseControl[] => {

    const isBond = PanelSettingsProductsTrait.isBonds(productType);
    const isCouponBonds = isBond &&
        (productCategory === ProductCategory.FixedRateBonds || productCategory === ProductCategory.FloatingRateBonds);
    const decimalsFour = !isBond;
    const percentage = isBond;

    const validators = isBond
        ? [Validators.required, Validators.min(1), Validators.max(100)]
        : [Validators.required];

    const result = [
        new DatepickerControl({
            name: AddEditProductPriceDateSelector,
            value: null,
            placeholder: 'Date',
            minDate: moment().format(environment.dateFormatLocalDate),
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Datepicker,
        }) as BaseControl
    ];

    if (mandatoryTypes.priceTypes.indexOf(FundProductPricesEnum.IssuePrice) >= 0) {
        result.push(new AmountControl({
            name: AddEditProductPriceIssuePriceSelector,
            value: null,
            placeholder: Helper.textsHelper().getProductPriceTypeName(FundProductPricesEnum.IssuePrice),
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: decimalsFour
        }));
    }

    if (mandatoryTypes.priceTypes.indexOf(FundProductPricesEnum.BidPrice) >= 0) {
        result.push(new AmountControl({
            name: AddEditProductPriceBidPriceSelector,
            value: null,
            placeholder: Helper.textsHelper().getProductPriceTypeName(FundProductPricesEnum.BidPrice),
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: decimalsFour,
            isPercentage: percentage
        }));
    }

    if (mandatoryTypes.priceTypes.indexOf(FundProductPricesEnum.PenalRedemptionPrice) >= 0) {
        result.push(new AmountControl({
            name: AddEditProductPricePenalRedemptionPriceSelector,
            value: null,
            placeholder: Helper.textsHelper().getProductPriceTypeName(FundProductPricesEnum.PenalRedemptionPrice),
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: decimalsFour,
            isPercentage: percentage
        }));
    }

    if (mandatoryTypes.priceTypes.indexOf(FundProductPricesEnum.NetAssetValue) >= 0) {
        result.push(new AmountControl({
            name: AddEditProductPriceNetAssetValueSelector,
            value: null,
            placeholder: Helper.textsHelper().getProductPriceTypeName(FundProductPricesEnum.NetAssetValue),
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: decimalsFour,
            isPercentage: percentage
        }));
    }

    if (mandatoryTypes.priceTypes.indexOf(FundProductPricesEnum.AskPrice) >= 0) {
        result.push(new AmountControl({
            name: AddEditProductPriceAskPriceSelector,
            value: null,
            placeholder: Helper.textsHelper().getProductPriceTypeName(FundProductPricesEnum.AskPrice),
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: decimalsFour,
            isPercentage: percentage
        }));
    }

    if (isCouponBonds) {
        result.push(new AmountControl({
            name: AddEditProductYieldToMaturitySelector,
            value: null,
            placeholder: 'Yield to Maturity',
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: false,
            isPercentage: true
        }));

        result.push(new AmountControl({
            name: AddEditProductAccruedInterestSelector,
            value: null,
            placeholder: 'Accrued Interest',
            validators: validators,
            componentType: ComponentTypesEnum.AmountInput,
            currency: null,
            fourDecimals: false,
            isPercentage: true
        }));
    }


    return result;
};

